<template>
  <div
    class="drawer-overlay"
    :class="{ visible }"
    @click.self="close"
  >
    <transition name="slide-left">
      <div
        v-if="visible"
        class="drawer-content"
      >

        <!-- =====================================================
             HEADER
        ====================================================== -->

        <div class="drawer-header">

          <div class="drawer-header-text">
            <h2 class="drawer-title">
              Добавить образование
            </h2>

            <p class="drawer-subtitle">
              Укажите учебное заведение, факультет и год окончания
            </p>
          </div>


          <button
            class="close-button"
            type="button"
            :disabled="isSaving"
            aria-label="Закрыть"
            @click="close"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5.5 5.5L14.5 14.5M14.5 5.5L5.5 14.5"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </button>

        </div>


        <!-- =====================================================
             BODY
        ====================================================== -->

        <div class="drawer-body">

          <div
            class="form-content"
            :class="{
              'form-content--disabled':
                isSaving
            }"
          >

            <!-- UNIVERSITY -->

            <div class="field-group">

              <label class="field-label">
                Учебное заведение
              </label>


              <EducationAutocomplete
                v-model="selectedUniversity"
              />


              <p
                v-if="errors.university"
                class="field-error"
              >
                {{ errors.university }}
              </p>

            </div>


            <!-- FACULTY -->

            <div class="field-group">

              <label
                class="field-label"
                for="education-faculty"
              >
                Факультет
              </label>


              <input
                id="education-faculty"
                v-model="faculty"
                class="custom-input"
                :class="{
                  'custom-input--error':
                    errors.faculty
                }"
                type="text"
                maxlength="120"
                placeholder="Например, факультет информационных технологий"
                :disabled="isSaving"
                @input="clearError('faculty')"
                @blur="validateFaculty"
              />


              <div class="field-meta">

                <p
                  v-if="errors.faculty"
                  class="field-error"
                >
                  {{ errors.faculty }}
                </p>


                <span
                  v-else
                  class="field-hint"
                >
                  Минимум 2 символа
                </span>


                <span class="counter">
                  {{ faculty.length }}/120
                </span>

              </div>

            </div>


            <!-- END DATE -->

            <div class="field-group">

              <label
                class="field-label"
                for="education-end-date"
              >
                Год окончания
              </label>


              <input
                id="education-end-date"
                v-model="endDate"
                class="custom-input year-input"
                :class="{
                  'custom-input--error':
                    errors.endDate
                }"
                type="text"
                inputmode="numeric"
                maxlength="4"
                placeholder="Например, 2024"
                :disabled="isSaving"
                @input="formatYear"
                @blur="validateEndDate"
                @keydown.enter.prevent="saveEducation"
              />


              <p
                v-if="errors.endDate"
                class="field-error"
              >
                {{ errors.endDate }}
              </p>


              <p
                v-else
                class="field-hint"
              >
                Введите год четырьмя цифрами
              </p>

            </div>

          </div>


          <!-- =====================================================
               SAVING OVERLAY
          ====================================================== -->

          <transition name="fade">

            <div
              v-if="isSaving"
              class="saving-overlay"
            >

              <div class="saving-card">

                <div class="saving-spinner"></div>


                <div class="saving-text">

                  <strong>
                    Сохраняем образование
                  </strong>

                  <span>
                    Это займёт несколько секунд
                  </span>

                </div>

              </div>

            </div>

          </transition>

        </div>


        <!-- =====================================================
             FOOTER
        ====================================================== -->

        <div class="drawer-footer">

          <button
            class="cancel-button"
            type="button"
            :disabled="isSaving"
            @click="close"
          >
            Отмена
          </button>


          <button
            class="save-btn"
            type="button"
            :disabled="
              !isFormValid ||
              isSaving
            "
            @click="saveEducation"
          >

            <template v-if="isSaving">

              <span class="button-spinner"></span>

              <span>
                Сохраняем...
              </span>

            </template>


            <template v-else>
              Сохранить
            </template>

          </button>

        </div>

      </div>
    </transition>
  </div>
</template>


<script setup>

import {
  computed,
  ref,
  watch
} from "vue";

import SummaryService
  from "../../api/SummaryService";

import EducationAutocomplete
  from "../profile/EducationAutocomplete.vue";


/* ============================================================
   PROPS / EMITS
============================================================ */

const props =
  defineProps({

    visible: {
      type: Boolean,
      default: false
    }

  });


const emit =
  defineEmits([
    "close",
    "save",
    "snackbar"
  ]);


/* ============================================================
   STATE
============================================================ */

const isSaving =
  ref(false);


const selectedUniversity =
  ref(null);

const faculty =
  ref("");

const endDate =
  ref("");


const errors =
  ref({

    university: "",
    faculty: "",
    endDate: ""

  });


/* ============================================================
   CONSTANTS
============================================================ */

const currentYear =
  new Date()
    .getFullYear();

const minAllowedYear =
  1900;

const maxAllowedYear =
  currentYear + 10;


/* ============================================================
   COMPUTED
============================================================ */

const universityName =
  computed(() => {

    const value =
      selectedUniversity.value;


    if (!value) {
      return "";
    }


    if (
      typeof value === "string"
    ) {

      return value.trim();

    }


    return String(

      value.name ||
      value.title ||
      value.university ||
      ""

    ).trim();

  });


const normalizedFaculty =
  computed(() => {

    return faculty.value
      .replace(
        /\s+/g,
        " "
      )
      .trim();

  });


const normalizedEndDate =
  computed(() => {

    return String(
      endDate.value ||
      ""
    )
      .replace(
        /\D/g,
        ""
      )
      .slice(
        0,
        4
      );

  });


const isFormValid =
  computed(() => {

    const year =
      Number(
        normalizedEndDate.value
      );


    return Boolean(

      universityName.value.length >= 2 &&

      normalizedFaculty.value.length >= 2 &&

      normalizedEndDate.value.length === 4 &&

      Number.isInteger(year) &&

      year >= minAllowedYear &&

      year <= maxAllowedYear

    );

  });


/* ============================================================
   WATCH
============================================================ */

watch(
  () => props.visible,
  (visible) => {

    if (visible) {
      clearErrors();
    }

  }
);


/* ============================================================
   ERRORS
============================================================ */

function clearErrors() {

  errors.value = {

    university: "",
    faculty: "",
    endDate: ""

  };

}


function clearError(field) {

  errors.value[field] =
    "";

}


/* ============================================================
   RESET
============================================================ */

function resetForm() {

  selectedUniversity.value =
    null;

  faculty.value =
    "";

  endDate.value =
    "";

  clearErrors();

}


/* ============================================================
   FORMAT YEAR
============================================================ */

function formatYear() {

  endDate.value =
    String(
      endDate.value ||
      ""
    )
      .replace(
        /\D/g,
        ""
      )
      .slice(
        0,
        4
      );


  clearError(
    "endDate"
  );

}


/* ============================================================
   VALIDATION
============================================================ */

function validateUniversity() {

  const name =
    universityName.value;


  if (!name) {

    errors.value.university =
      "Выберите учебное заведение";

    return false;

  }


  if (
    name.length < 2
  ) {

    errors.value.university =
      "Название учебного заведения слишком короткое";

    return false;

  }


  errors.value.university =
    "";

  return true;

}


function validateFaculty() {

  const value =
    normalizedFaculty.value;


  if (!value) {

    errors.value.faculty =
      "Укажите факультет или направление";

    return false;

  }


  if (
    value.length < 2
  ) {

    errors.value.faculty =
      "Название факультета слишком короткое";

    return false;

  }


  if (
    value.length > 120
  ) {

    errors.value.faculty =
      "Название факультета слишком длинное";

    return false;

  }


  errors.value.faculty =
    "";

  return true;

}


function validateEndDate() {

  const value =
    normalizedEndDate.value;


  if (!value) {

    errors.value.endDate =
      "Укажите год окончания";

    return false;

  }


  if (
    value.length !== 4
  ) {

    errors.value.endDate =
      "Год должен состоять из четырёх цифр";

    return false;

  }


  const year =
    Number(value);


  if (
    !Number.isInteger(year)
  ) {

    errors.value.endDate =
      "Введите корректный год";

    return false;

  }


  if (
    year < minAllowedYear
  ) {

    errors.value.endDate =
      `Год не может быть раньше ${minAllowedYear}`;

    return false;

  }


  if (
    year > maxAllowedYear
  ) {

    errors.value.endDate =
      `Год не может быть позже ${maxAllowedYear}`;

    return false;

  }


  errors.value.endDate =
    "";

  return true;

}


function validateForm() {

  const universityValid =
    validateUniversity();

  const facultyValid =
    validateFaculty();

  const endDateValid =
    validateEndDate();


  return (

    universityValid &&
    facultyValid &&
    endDateValid

  );

}


/* ============================================================
   CLOSE
============================================================ */

function close() {

  if (
    isSaving.value
  ) {
    return;
  }


  resetForm();

  emit(
    "close"
  );

}


/* ============================================================
   SAVE
============================================================ */

async function saveEducation() {

  if (
    isSaving.value
  ) {
    return;
  }


  if (
    !validateForm()
  ) {

    emit(
      "snackbar",
      {
        message:
          "Проверьте заполненные поля",

        type:
          "error"
      }
    );

    return;

  }


  const payload = {

    university:
      universityName.value,

    faculty:
      normalizedFaculty.value,

    end_date:
      normalizedEndDate.value

  };


  isSaving.value =
    true;


  try {

    const response =
      await SummaryService
        .addEducation(
          payload
        );


    const responseEducation =

      response?.education ||

      response?.data?.education ||

      response?.data ||

      response ||

      {};


    const savedEducation = {

      ...payload,

      ...responseEducation

    };


    console.log(
      "✅ Образование успешно добавлено:",
      savedEducation
    );


    emit(
      "save",
      savedEducation
    );


    emit(
      "snackbar",
      {

        message:
          "Образование успешно добавлено",

        type:
          "success"

      }
    );


    resetForm();

    emit(
      "close"
    );

  } catch (error) {

    console.error(
      "❌ Ошибка при сохранении образования:",
      error
    );


    const errorMessage =

      error?.response?.data?.message ||

      error?.message ||

      "Не удалось сохранить образование";


    emit(
      "snackbar",
      {

        message:
          errorMessage,

        type:
          "error"

      }
    );

  } finally {

    isSaving.value =
      false;

  }

}

</script>


<style scoped>

/* ============================================================
   OVERLAY
============================================================ */

.drawer-overlay {
  position: fixed;
  inset: 0;

  z-index: 1000;

  display: flex;
  align-items: stretch;
  justify-content: flex-start;

  background:
    rgba(17, 21, 28, .34);

  backdrop-filter:
    blur(2px);

  -webkit-backdrop-filter:
    blur(2px);

  opacity: 0;

  pointer-events: none;

  transition:
    opacity .24s ease;
}


.drawer-overlay.visible {
  opacity: 1;

  pointer-events: auto;
}


/* ============================================================
   DRAWER
============================================================ */

.drawer-content {
  width:
    min(
      720px,
      100%
    );

  height:
    100%;

  display: flex;

  flex-direction:
    column;

  box-sizing:
    border-box;

  overflow:
    hidden;

  background:
    #fbfbfc;

  border-right:
    1px solid
    rgba(28, 38, 55, .06);

  box-shadow:
    24px
    0
    70px
    rgba(20, 30, 48, .16);
}


/* ============================================================
   HEADER
============================================================ */

.drawer-header {
  min-height:
    96px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    24px;

  box-sizing:
    border-box;

  padding:
    22px
    30px;

  background:
    rgba(255, 255, 255, .86);

  border-bottom:
    1px solid
    rgba(25, 35, 50, .06);

  backdrop-filter:
    blur(16px);

  -webkit-backdrop-filter:
    blur(16px);
}


.drawer-header-text {
  min-width:
    0;
}


.drawer-title {
  margin:
    0;

  color:
    #30343b;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    19px;

  line-height:
    1.25;

  letter-spacing:
    -.35px;
}


.drawer-subtitle {
  max-width:
    430px;

  margin:
    6px
    0
    0;

  color:
    #8d929a;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    11px;

  line-height:
    1.5;
}


/* ============================================================
   CLOSE
============================================================ */

.close-button {
  width:
    38px;

  height:
    38px;

  flex:
    0
    0
    38px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  box-sizing:
    border-box;

  padding:
    0;

  color:
    #68707b;

  background:
    rgba(35, 45, 65, .045);

  border:
    1px solid
    rgba(35, 45, 65, .055);

  border-radius:
    12px;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    transform .18s ease,
    opacity .18s ease;
}


.close-button svg {
  width:
    17px;

  height:
    17px;
}


.close-button:hover:not(:disabled) {
  background:
    rgba(35, 45, 65, .075);

  transform:
    scale(1.04);
}


.close-button:disabled {
  opacity:
    .35;

  cursor:
    not-allowed;
}


/* ============================================================
   BODY
============================================================ */

.drawer-body {
  position:
    relative;

  flex:
    1;

  min-height:
    0;

  box-sizing:
    border-box;

  padding:
    30px;

  overflow-y:
    auto;

  scrollbar-width:
    thin;

  scrollbar-color:
    rgba(120, 125, 135, .18)
    transparent;
}


.drawer-body::-webkit-scrollbar {
  width:
    5px;
}


.drawer-body::-webkit-scrollbar-thumb {
  background:
    rgba(120, 125, 135, .18);

  border-radius:
    999px;
}


/* ============================================================
   FORM
============================================================ */

.form-content {
  width:
    100%;

  display:
    flex;

  flex-direction:
    column;

  gap:
    25px;

  box-sizing:
    border-box;

  padding:
    26px;

  background:
    rgba(255, 255, 255, .88);

  border:
    1px solid
    rgba(31, 42, 60, .055);

  border-radius:
    22px;

  box-shadow:
    0
    12px
    34px
    rgba(30, 45, 70, .045);

  transition:
    opacity .2s ease,
    filter .2s ease;
}


.form-content--disabled {
  opacity:
    .5;

  filter:
    grayscale(.12);

  pointer-events:
    none;
}


/* ============================================================
   FIELDS
============================================================ */

.field-group {
  display:
    flex;

  flex-direction:
    column;

  gap:
    9px;
}


.field-label {
  color:
    #535a65;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  line-height:
    1.35;
}


/* ============================================================
   INPUT
============================================================ */

.custom-input {
  width:
    100%;

  height:
    50px;

  box-sizing:
    border-box;

  padding:
    0
    15px;

  color:
    #2f333a;

  background:
    #f4f5f7;

  border:
    1px solid
    rgba(35, 45, 60, .035);

  border-radius:
    14px;

  outline:
    none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    13px;

  transition:
    background-color .18s ease,
    border-color .18s ease,
    box-shadow .18s ease;
}


.custom-input:hover:not(:disabled) {
  background:
    #f0f2f5;
}


.custom-input:focus {
  background:
    #ffffff;

  border-color:
    rgba(52, 127, 231, .48);

  box-shadow:
    0
    0
    0
    4px
    rgba(52, 127, 231, .08);
}


.custom-input::placeholder {
  color:
    #a2a7af;
}


.custom-input--error {
  background:
    rgba(220, 72, 72, .035);

  border-color:
    rgba(220, 72, 72, .50);
}


.custom-input--error:focus {
  border-color:
    #dc4848;

  box-shadow:
    0
    0
    0
    4px
    rgba(220, 72, 72, .08);
}


.year-input {
  max-width:
    190px;
}


/* ============================================================
   META
============================================================ */

.field-error,
.field-hint {
  margin:
    0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.4;
}


.field-error {
  color:
    #d95757;
}


.field-hint {
  color:
    #9ca1a9;
}


.field-meta {
  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    12px;
}


.counter {
  margin-left:
    auto;

  color:
    #a1a6ae;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;
}


/* ============================================================
   SAVING
============================================================ */

.saving-overlay {
  position:
    absolute;

  inset:
    0;

  z-index:
    10;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  padding:
    24px;

  background:
    rgba(250, 251, 252, .52);

  backdrop-filter:
    blur(4px);

  -webkit-backdrop-filter:
    blur(4px);
}


.saving-card {
  display:
    flex;

  align-items:
    center;

  gap:
    13px;

  padding:
    15px
    18px;

  background:
    rgba(255, 255, 255, .96);

  border:
    1px solid
    rgba(45, 60, 80, .07);

  border-radius:
    16px;

  box-shadow:
    0
    16px
    38px
    rgba(25, 40, 65, .12);
}


.saving-spinner {
  width:
    26px;

  height:
    26px;

  flex-shrink:
    0;

  box-sizing:
    border-box;

  border:
    3px solid
    rgba(52, 127, 231, .14);

  border-top-color:
    #347fe7;

  border-radius:
    50%;

  animation:
    spin
    .65s
    linear
    infinite;
}


.saving-text {
  display:
    flex;

  flex-direction:
    column;

  gap:
    3px;
}


.saving-text strong {
  color:
    #383d45;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    12px;
}


.saving-text span {
  color:
    #9399a2;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;
}


/* ============================================================
   FOOTER
============================================================ */

.drawer-footer {
  min-height:
    78px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    flex-end;

  gap:
    10px;

  box-sizing:
    border-box;

  padding:
    14px
    30px;

  background:
    rgba(255, 255, 255, .92);

  border-top:
    1px solid
    rgba(30, 40, 55, .06);

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);
}


/* ============================================================
   BUTTONS
============================================================ */

.cancel-button,
.save-btn {
  height:
    42px;

  box-sizing:
    border-box;

  padding:
    0
    20px;

  border:
    none;

  border-radius:
    12px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    color .18s ease,
    opacity .18s ease,
    box-shadow .18s ease,
    transform .18s ease;
}


.cancel-button {
  color:
    #5f6670;

  background:
    #f1f2f4;
}


.cancel-button:hover:not(:disabled) {
  background:
    #e9ebee;
}


.save-btn {
  min-width:
    132px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    8px;

  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #347fe7,
      #2769c7
    );

  box-shadow:
    0
    7px
    18px
    rgba(52, 127, 231, .18);
}


.save-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    9px
    22px
    rgba(52, 127, 231, .24);
}


.save-btn:disabled {
  color:
    #969ca4;

  background:
    #e1e3e7;

  box-shadow:
    none;

  cursor:
    not-allowed;
}


.cancel-button:disabled {
  opacity:
    .45;

  cursor:
    not-allowed;
}


/* ============================================================
   BUTTON SPINNER
============================================================ */

.button-spinner {
  width:
    16px;

  height:
    16px;

  box-sizing:
    border-box;

  border:
    2px solid
    rgba(255, 255, 255, .36);

  border-top-color:
    #ffffff;

  border-radius:
    50%;

  animation:
    spin
    .65s
    linear
    infinite;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.drawer-overlay {
  background:
    rgba(0, 0, 0, .62);
}


html[data-theme="dark"]
.drawer-content {
  background:
    #1b1d21;

  border-right-color:
    rgba(255, 255, 255, .05);

  box-shadow:
    24px
    0
    70px
    rgba(0, 0, 0, .38);
}


html[data-theme="dark"]
.drawer-header {
  background:
    rgba(28, 30, 35, .92);

  border-bottom-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.drawer-title {
  color:
    #f0f1f3;
}


html[data-theme="dark"]
.drawer-subtitle {
  color:
    #858a93;
}


html[data-theme="dark"]
.close-button {
  color:
    #aeb3bb;

  background:
    rgba(255, 255, 255, .045);

  border-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.close-button:hover:not(:disabled) {
  background:
    rgba(255, 255, 255, .075);
}


html[data-theme="dark"]
.form-content {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, .065);

  box-shadow:
    0
    16px
    40px
    rgba(0, 0, 0, .18);
}


html[data-theme="dark"]
.field-label {
  color:
    #b6bac2;
}


html[data-theme="dark"]
.custom-input {
  color:
    #e8e9eb;

  background:
    #292b30;

  border-color:
    rgba(255, 255, 255, .045);
}


html[data-theme="dark"]
.custom-input:hover:not(:disabled) {
  background:
    #2d3035;
}


html[data-theme="dark"]
.custom-input:focus {
  background:
    #27292e;

  border-color:
    rgba(91, 158, 255, .5);

  box-shadow:
    0
    0
    0
    4px
    rgba(91, 158, 255, .075);
}


html[data-theme="dark"]
.custom-input::placeholder {
  color:
    #747981;
}


html[data-theme="dark"]
.custom-input--error {
  background:
    rgba(220, 80, 80, .06);

  border-color:
    rgba(255, 105, 105, .48);
}


html[data-theme="dark"]
.field-error {
  color:
    #ff7c7c;
}


html[data-theme="dark"]
.field-hint,
html[data-theme="dark"]
.counter {
  color:
    #747981;
}


html[data-theme="dark"]
.saving-overlay {
  background:
    rgba(20, 22, 26, .56);
}


html[data-theme="dark"]
.saving-card {
  background:
    #24272c;

  border-color:
    rgba(255, 255, 255, .07);

  box-shadow:
    0
    16px
    40px
    rgba(0, 0, 0, .34);
}


html[data-theme="dark"]
.saving-text strong {
  color:
    #eceef1;
}


html[data-theme="dark"]
.saving-text span {
  color:
    #858a93;
}


html[data-theme="dark"]
.drawer-footer {
  background:
    rgba(28, 30, 35, .94);

  border-top-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.cancel-button {
  color:
    #c5c8ce;

  background:
    #292c31;
}


html[data-theme="dark"]
.cancel-button:hover:not(:disabled) {
  background:
    #303339;
}


html[data-theme="dark"]
.save-btn:disabled {
  color:
    #727780;

  background:
    #303238;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 640px) {

  .drawer-content {
    width:
      100%;
  }


  .drawer-header {
    min-height:
      82px;

    padding:
      17px
      18px;
  }


  .drawer-title {
    font-size:
      17px;
  }


  .drawer-subtitle {
    font-size:
      10px;
  }


  .close-button {
    width:
      34px;

    height:
      34px;

    flex-basis:
      34px;

    border-radius:
      10px;
  }


  .drawer-body {
    padding:
      18px;
  }


  .form-content {
    padding:
      20px;

    border-radius:
      18px;

    gap:
      22px;
  }


  .drawer-footer {
    padding:
      12px
      18px;
  }


  .cancel-button {
    display:
      none;
  }


  .save-btn {
    width:
      100%;

    height:
      44px;
  }


  .year-input {
    max-width:
      100%;
  }

}


/* ============================================================
   ANIMATIONS
============================================================ */

@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}


.slide-left-enter-active,
.slide-left-leave-active {
  transition:
    transform
    .30s
    cubic-bezier(
      .22,
      1,
      .36,
      1
    ),
    opacity
    .22s
    ease;
}


.slide-left-enter-from,
.slide-left-leave-to {
  transform:
    translateX(-100%);

  opacity:
    0;
}


.slide-left-enter-to,
.slide-left-leave-from {
  transform:
    translateX(0);

  opacity:
    1;
}


.fade-enter-active,
.fade-leave-active {
  transition:
    opacity
    .2s
    ease;
}


.fade-enter-from,
.fade-leave-to {
  opacity:
    0;
}

</style>